/**
 * How much load to add per progression step, keyed on equipment and unit
 * system. The nominal steps a gym actually stocks are not exact conversions of
 * each other, so the two tables are independent rather than one derived from
 * the other.
 *
 * **Imperial steps are 5lb or 10lb, and nothing in between.** A barbell moves
 * in pairs of plates, so the smallest honest jump is a pair of 5s — 10lb on the
 * bar. The old table said 5lb, which is a pair of 2.5s, and most gyms do not
 * stock them: it prescribed loads the lifter could not actually build. Selectorised
 * stacks (cable, machine) pin in 10lb notches for the same reason. Dumbbells
 * and kettlebells come off the rack in 5lb pairs, and that step is per hand.
 *
 * `null` marks reps-only equipment: nothing to load incrementally, so the
 * engine progresses reps instead (see `engine.js`'s handling of
 * `getIncrementKg() == null`).
 */
const KG_STEPS = {
  barbell: 2.5, // a pair of 1.25kg plates
  barbell_lower: 5, // lower-body barbell compounds (squat, deadlift) double the upper-body step
  dumbbell: 2, // per hand
  cable: 2.5,
  machine: 5, // one pin, approximated — real stacks vary by machine
  kettlebell: 2,
  band: null,
  bodyweight: null,
  other: 2.5,
};

const LB_STEPS = {
  barbell: 10, // a pair of 5lb plates — the smallest jump most gyms can actually load
  // Squats and deadlifts take a double step in kilograms, where the base step is
  // a 2.5kg pair. In pounds the base step is already 10lb, and 20lb in a session
  // is a bigger jump than this engine will make, so the two collapse to one.
  barbell_lower: 10,
  dumbbell: 5, // per hand — one step up the rack
  cable: 10, // one pin on a selectorised stack
  machine: 10,
  kettlebell: 5,
  band: null,
  bodyweight: null,
  other: 5,
};

/**
 * The largest jump the kilogram table allows in a single session.
 *
 * `engine.js` gives a lifter with reps to spare a *double* step. In kilograms
 * that is right for the finer rows — a barbell's base step is a 1.25kg pair, and
 * two of them is an ordinary 2.5kg-pair jump — but not for rows that already sit
 * at a full 5kg, which would double to 10kg.
 */
const KG_MAX_STEP = 5;

const LB_TO_KG = 0.453592;

function isLowerBodyCompound(equipment, muscleGroup) {
  return equipment === 'barbell' && muscleGroup === 'legs';
}

/** The table row for this exercise, in that table's own nominal units. */
function nominalStep({ equipment, muscleGroup, unitSystem }) {
  const imperial = unitSystem === 'imperial';
  const table = imperial ? LB_STEPS : KG_STEPS;
  const key = isLowerBodyCompound(equipment, muscleGroup) ? 'barbell_lower' : (equipment || 'other');
  return {
    imperial,
    step: key in table ? table[key] : table.other,
  };
}

/**
 * Returns the load step in kg (the unit `sets.weight_kg` is stored in),
 * regardless of the caller's display unit system — or `null` for equipment
 * that has no incremental load to add.
 */
export function getIncrementKg({ equipment, muscleGroup, unitSystem = 'metric' }) {
  const { imperial, step } = nominalStep({ equipment, muscleGroup, unitSystem });
  if (step == null) return null;
  return imperial ? Math.round(step * LB_TO_KG * 100) / 100 : step;
}

/**
 * How many steps a single session may add.
 *
 * **In pounds, always one.** The rows above are not a floor to build on, they
 * are the recommendation itself: 5lb up the dumbbell rack, 10lb on a bar or a
 * pin. Doubling any of them lands outside that granularity — a 20lb jump on a
 * bench, 10lb per hand on dumbbells — so the pound table has no second gear.
 *
 * **In kilograms, two where the step is fine enough to take it.** A metric
 * barbell's base step is a 1.25kg pair, roughly half of what a pound gym can
 * load at all, so doubling it produces 5kg — about 11lb, which is the imperial
 * single step. The two systems land in the same place; kilograms just have one
 * more gear on the way there, because 2.5lb plates are not a thing most gyms
 * stock and 1.25kg plates are.
 */
export function getMaxLoadSteps({ equipment, muscleGroup, unitSystem = 'metric' }) {
  const { imperial, step } = nominalStep({ equipment, muscleGroup, unitSystem });
  if (step == null || imperial) return 1;
  return step * 2 <= KG_MAX_STEP ? 2 : 1;
}
